import { staticTable } from "@-/dev.improve.haniverse.database.schemas.static._lib.table";
import { sentenceTable } from "@-/dev.improve.haniverse.database.schemas.static.schema._tables.sentence";
import { wordTable } from "@-/dev.improve.haniverse.database.schemas.static.schema._tables.word";
import { index, integer } from "@-/dev.improve.haniverse.lib.database.table";

/**
 * Maps each sentence to the words it contains, in reading order. One row per (sentence, word)
 * occurrence, from build-time jieba segmentation.
 *
 * Lets a word-keyed lookup ("which sentences contain this word?") run as an indexed equality
 * on `word_id`, instead of the `sentence LIKE '%word%'` substring match that forces a full
 * table scan. This mirrors `word_grapheme`, which exists for exactly the same reason on the
 * word side.
 *
 * The segmentation was already computed at build time and then discarded, so the app re-ran
 * jieba on-device for every rendered sentence. Persisting it demotes that on-device pass to a
 * fallback (`use-sentence-words-query`) rather than removing it — a sentence a user annotated
 * that later leaves the corpus has no rows here and must still be segmented from its stored
 * text.
 *
 * NOT a complete tokenization: `word` rows are length >= 2 by construction (single graphemes
 * are never word rows), so single-grapheme tokens and punctuation produce no row here.
 */
export const sentenceWordTable = staticTable(
  "sentence_word",
  {
    /** 0-based codepoint offset of this word within `sentence.sentence`. */
    charOffset: integer("char_offset").notNull(),
    sentenceId: integer("sentence_id")
      .notNull()
      .references(() => sentenceTable.id),
    /** 0-based position among the sentence's jieba tokens (including ones with no word row). */
    tokenIndex: integer("token_index").notNull(),
    wordId: integer("word_id")
      .notNull()
      .references(() => wordTable.id),
  },
  (table) => [
    index("sentence_word_sentence_id_idx").on(table.sentenceId),
    index("sentence_word_word_id_idx").on(table.wordId),
  ],
);

export type SentenceWord = typeof sentenceWordTable.$inferSelect;
